import { useTranslation } from 'react-i18next';

import { CustomModal } from 'components/shared';
import { CustomModalProps } from 'components/shared/CustomModal/CustomModal';
import { useErrorDescriptions } from 'constants/errorDescriptions';
import { useModal } from 'hooks/useModal';
import { OrderDetailsDTO } from 'interfaces';
import { HTTPService } from 'service';
import { logNetworkError } from 'utils/logNetworkError';

import styles from './RejectOrderModal.module.css';

type RejectOrderModalProps = CustomModalProps & {
   selectedOrderId: string;
   isOrderNonstandard?: boolean;
   onSuccessRejectOrder: (updatedOrder: OrderDetailsDTO) => void;
};

const RejectOrderModal = ({
   selectedOrderId,
   isOrderNonstandard,
   onSuccessRejectOrder,
   onClose,
   ...props
}: RejectOrderModalProps) => {
   const { t } = useTranslation();
   const { isLoading, errorInfo, setIsLoading, setErrorInfo, closeModalHandler } = useModal({
      onClose,
   });

   const { DEFAULT_ERROR_DESCRIPTIONS } = useErrorDescriptions();

   const handleRejectOrder = () => {
      setIsLoading(true);
      HTTPService.changeOrderStatus(selectedOrderId, 'REFUSE')
         .then(response => {
            onSuccessRejectOrder(response.data);
            closeModalHandler();
         })
         .catch(error => {
            setErrorInfo(DEFAULT_ERROR_DESCRIPTIONS.UNKNOWN);
            logNetworkError(error);
         })
         .finally(() => setIsLoading(false));
   };

   const title = isOrderNonstandard
      ? t('order_details_page.reject_non_standard_order')
      : t('order_details_page.reject_order');

   return (
      <CustomModal
         {...props}
         icon="ALERT_TRIANGLE"
         innerTitle={title}
         error={errorInfo}
         modalContentClassName={styles.modalContent}
         isLoading={isLoading}
         onClose={closeModalHandler}
         primaryButtonProps={{
            text: title,
            variant: 'filled-danger',
            onClick: handleRejectOrder,
         }}
         secondaryButtonProps={{
            text: t('common.cancel'),
            variant: 'outlined-danger',
            onClick: closeModalHandler,
         }}
      >
         <>
            <p className={styles.plainText}>
               {isOrderNonstandard
                  ? t('reject_order_modal.confirm_nonstandard')
                  : t('reject_order_modal.confirm_standard')}
            </p>
            <p className={styles.boldBlackText}>{t('cancel_pallet_report_modal.irreversible')}</p>
         </>
      </CustomModal>
   );
};

export default RejectOrderModal;
